/* eslint-disable max-lines-per-function -- Reservation actions share one mutation/invalidation context. */
import { useCallback } from 'react';
import { useRouteContext } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { reservations, parseApiError, ApiErrorCode } from '@ooh/contracts';
import type { AvailabilityCell, ApiError } from '@ooh/contracts';
import type { UnitAvailabilityRow } from '@ooh/contracts';
import { useHasPermission } from '../../../hooks/use-has-permission';
import {
  inclusiveDays,
  mapApiErrorCodeToReason,
  type AvailabilityPeriod,
  type CellSelection,
} from '@ooh/screens';

export { inclusiveDays };
import type { WithCallWithAuth } from './api';
import type { DisplayAvailabilityPeriod } from './availability-tab-grid';

/**
 * True when `cell` is held by another user (RESERVED-OTHER) AND the caller
 * holds `reservation.force_release` — the shared ownership+permission check
 * `handleUpgradeHard`/`handleCloseSale`/`handleRelease` each need to decide
 * whether `force: true` is sent to bypass the server's holder-only check
 * (CU-86cbc3r8b QA fix). Extracted to one definition so a future edge-case
 * fix cannot land in only one of the three call sites (qual-duplicated-logic).
 * The permission check is included here (not left to UI gating alone) so
 * this can never send `force: true` for a caller who lacks the permission,
 * even if the popover's own visibility gate ever drifts (qual-dry-violation
 * fix — mirrors `computeForceFlag`'s isOwn-and-permission formula).
 */
function isForceRequired(
  cell: Pick<AvailabilityCell, 'status'>,
  canForceRelease: boolean,
): boolean {
  return cell.status === 'RESERVED-OTHER' && canForceRelease;
}

type ReservationToastKey =
  | 'workspace.availabilityTab.toasts.reservationConflict'
  | 'workspace.availabilityTab.toasts.optionExtensionImpossible'
  | 'workspace.availabilityTab.toasts.staleState'
  | 'workspace.availabilityTab.toasts.notFound'
  | 'workspace.availabilityTab.toasts.forbidden'
  | 'workspace.availabilityTab.toasts.validationError'
  | 'workspace.availabilityTab.toasts.availabilityWindowBlocked'
  | 'workspace.availabilityTab.toasts.genericError';

// Toast key for each `mapApiErrorCodeToReason` bucket this flow shares with
// batch-reserve's `error-toast.ts` — own namespace/copy, shared errorCode->reason switch.
// minWindowViolation falls back to the generic error key here: the real, specific message
// (with the actual day count) is shown by the dedicated MIN_WINDOW_VIOLATION branch in
// runAction before this map is ever consulted — this entry only covers the defensive case
// where the error body is missing the expected minWindowDays/selectedDays fields.
const TOAST_KEY_BY_REASON: Record<
  ReturnType<typeof mapApiErrorCodeToReason>,
  ReservationToastKey
> = {
  reservationConflict: 'workspace.availabilityTab.toasts.reservationConflict',
  staleState: 'workspace.availabilityTab.toasts.staleState',
  notFound: 'workspace.availabilityTab.toasts.notFound',
  forbidden: 'workspace.availabilityTab.toasts.forbidden',
  validationError: 'workspace.availabilityTab.toasts.validationError',
  minWindowViolation: 'workspace.availabilityTab.toasts.genericError',
  generic: 'workspace.availabilityTab.toasts.genericError',
};

function errorCodeToToastKey(errorCode: ApiErrorCode): ReservationToastKey {
  switch (errorCode) {
    case ApiErrorCode.OPTION_EXTENSION_IMPOSSIBLE:
      return 'workspace.availabilityTab.toasts.optionExtensionImpossible';
    case ApiErrorCode.AVAILABILITY_WINDOW_BLOCKED:
      return 'workspace.availabilityTab.toasts.availabilityWindowBlocked';
    default:
      return TOAST_KEY_BY_REASON[mapApiErrorCodeToReason(errorCode)];
  }
}

function selectedPeriodRange(selection: CellSelection, periods: DisplayAvailabilityPeriod[]) {
  const selectedPeriods = selection.periodIds
    .map((periodId) => periods.find((period) => period.id === periodId))
    .filter((period): period is DisplayAvailabilityPeriod => period !== undefined);
  const first = selectedPeriods[0];
  const last = selectedPeriods[selectedPeriods.length - 1];
  if (!first || !last) return null;
  return { dateFrom: first.start, dateTo: last.end };
}

/**
 * Checks if an API error body has queueable: true.
 * The `queueable` field is NOT parsed by parseApiError() — it lives at the top level of
 * the raw error body. This function inspects the raw body directly.
 * @see research-web.md Gotchas §1
 */
export function isQueueableConflict(error: unknown): boolean {
  const body = (error as ApiError).body;
  return (
    typeof body === 'object' &&
    body !== null &&
    (body as { queueable?: boolean }).queueable === true
  );
}

/**
 * Extracts { minWindowDays, selectedDays } from a MIN_WINDOW_VIOLATION error body, or null
 * when the error is not that shape. Like `isQueueableConflict`, these fields are NOT parsed
 * by parseApiError() — read the raw body directly (CU-86caypwzm).
 */
export function minWindowViolationDetails(
  error: unknown,
): { minWindowDays: number; selectedDays: number } | null {
  const body = (error as ApiError).body;
  if (typeof body !== 'object' || body === null) return null;
  const { minWindowDays, selectedDays } = body as {
    minWindowDays?: unknown;
    selectedDays?: unknown;
  };
  if (typeof minWindowDays !== 'number' || typeof selectedDays !== 'number') return null;
  return { minWindowDays, selectedDays };
}

export function useAvailabilityReservationActions({
  offerId,
  canEdit,
  periods,
  units,
  getCell,
  onActionComplete,
  onQueueableConflict,
}: {
  offerId: number;
  canEdit: boolean;
  periods: DisplayAvailabilityPeriod[];
  units: UnitAvailabilityRow[];
  getCell: (unitId: string, periodId: string) => AvailabilityCell | null;
  onActionComplete: () => void;
  /** Called instead of toasting when a 409 RESERVATION_CONFLICT has queueable: true. */
  onQueueableConflict?: (unitId: string, dateFrom: string, dateTo: string) => void;
}) {
  const { t } = useTranslation('offers');
  const routeContext = useRouteContext({ from: '__root__' }) as { auth: WithCallWithAuth };
  const canManageReservation = useHasPermission('reservation.manage');
  const canForceReleaseReservation = useHasPermission('reservation.force_release');
  const canCreateReservation = canManageReservation;
  const canCancelReservation = canManageReservation;
  const canUpgradeHardReservation = canManageReservation;
  const canCloseSaleReservation = canManageReservation;

  const runAction = useCallback(
    (
      action: () => Promise<unknown>,
      conflictContext?: { unitId: string; dateFrom: string; dateTo: string },
    ) => {
      void action()
        .then(onActionComplete)
        .catch((error: unknown) => {
          const parsed = parseApiError(error);
          // Check for queueable conflict BEFORE mapping to toast key
          if (
            parsed.errorCode === ApiErrorCode.RESERVATION_CONFLICT &&
            isQueueableConflict(error) &&
            onQueueableConflict &&
            conflictContext
          ) {
            onQueueableConflict(
              conflictContext.unitId,
              conflictContext.dateFrom,
              conflictContext.dateTo,
            );
            return;
          }
          // MIN_WINDOW_VIOLATION carries the actual required/selected day counts on the raw
          // body (not surfaced by parseApiError) — show them instead of a generic message
          // so the user knows exactly why the reservation was rejected (CU-86caypwzm).
          // Note: handleReserve (this hook's single-cell POST /reservations path) is not
          // currently wired to any UI trigger — the real, reachable exact-day-count fix
          // lives in error-toast.ts's showBatchReserveConflictToasts, since the grid
          // always goes through batch-reserve. This branch is kept for when/if a future
          // non-batch trigger calls handleReserve directly.
          if (parsed.errorCode === ApiErrorCode.MIN_WINDOW_VIOLATION) {
            const details = minWindowViolationDetails(error);
            if (details) {
              // `count` drives i18next's _one/_few/_many/_other plural suffix on
              // minWindowDays (e.g. "1 dzień" vs "7 dni") — see error-toast.ts's
              // identical batchReserveConflictMinWindowViolationExact usage.
              toast.error(
                t('workspace.availabilityTab.toasts.minWindowViolation', {
                  minWindowDays: details.minWindowDays,
                  selectedDays: details.selectedDays,
                  count: details.minWindowDays,
                }),
              );
              return;
            }
          }
          const key = errorCodeToToastKey(parsed.errorCode);
          toast.error(t(key));
        });
    },
    [onActionComplete, onQueueableConflict, t],
  );

  const handleCellClick = useCallback(
    (unitId: string, period: AvailabilityPeriod) => {
      if (!canEdit) return null;
      return { unitId, periodIds: [period.id] } satisfies CellSelection;
    },
    [canEdit],
  );

  const handleReserve = useCallback(
    (selection: CellSelection, status: 'SOFT_RESERVATION' | 'OPTION') => {
      const range = selectedPeriodRange(selection, periods);
      const unit = units.find((candidate) => String(candidate.unitId) === selection.unitId);
      if (!range || !unit || !canCreateReservation) return;

      runAction(
        () =>
          routeContext.auth.callWithAuth(reservations.create, {
            mediaUnitId: unit.mediaUnitId,
            offerId,
            status,
            dateFrom: new Date(`${range.dateFrom}T00:00:00.000Z`),
            dateTo: new Date(`${range.dateTo}T00:00:00.000Z`),
            targetVisibilityDays:
              status === 'OPTION' ? inclusiveDays(range.dateFrom, range.dateTo) : undefined,
          }),
        { unitId: selection.unitId, dateFrom: range.dateFrom, dateTo: range.dateTo },
      );
    },
    [canCreateReservation, offerId, periods, routeContext.auth, runAction, units],
  );

  const handleUpgradeHard = useCallback(
    (selection: CellSelection) => {
      const cell = getCell(selection.unitId, selection.periodIds[0] ?? '');
      if (!cell?.reservationId || !canUpgradeHardReservation) return;
      runAction(() =>
        routeContext.auth.callWithAuth(reservations.upgradeHard, {
          id: cell.reservationId,
          expectedStatus: 'SOFT_RESERVATION',
          // See isForceRequired's doc comment — mirrors `computeForceFlag`'s
          // isOwn-derived force semantics.
          force: isForceRequired(cell, canForceReleaseReservation) ? true : undefined,
        }),
      );
    },
    [canForceReleaseReservation, canUpgradeHardReservation, getCell, routeContext.auth, runAction],
  );

  const handleCloseSale = useCallback(
    (selection: CellSelection) => {
      const cell = getCell(selection.unitId, selection.periodIds[0] ?? '');
      const expectedStatus = cell?.reservationStatus;
      if (
        !cell?.reservationId ||
        (expectedStatus !== 'SOFT_RESERVATION' && expectedStatus !== 'HARD_RESERVATION') ||
        !canCloseSaleReservation
      ) {
        return;
      }
      runAction(() =>
        routeContext.auth.callWithAuth(reservations.closeSale, {
          id: cell.reservationId,
          expectedStatus,
          // See isForceRequired's doc comment — same ownership-derived semantics.
          force: isForceRequired(cell, canForceReleaseReservation) ? true : undefined,
        }),
      );
    },
    [canCloseSaleReservation, canForceReleaseReservation, getCell, routeContext.auth, runAction],
  );

  const handleRelease = useCallback(
    // `force` explicitly picks Anuluj (false) vs. Wymuś zwolnienie (true) for the consolidated
    // INSPECT popover's two distinct quick actions (task 4.4) — omitted, it falls back to the
    // pre-existing auto-detect (force whenever the caller holds force_release), preserving the
    // single combined "release" button's original behavior for any other caller.
    //
    // CU-86cbc3r8b QA fix: Anuluj's explicit `force: false` used to reach the server
    // verbatim, so clicking it on another user's hold always 403'd even though
    // resolveQuickActionFlags's ownership gate only shows Anuluj there when the caller
    // holds reservation.force_release. The wire-level force now follows ownership
    // (RESERVED-OTHER -> true) instead of the raw Anuluj/Wymuś intent, so the visible
    // action always matches what the server will accept — see handleUpgradeHard's
    // identical comment.
    (selection: CellSelection, force?: boolean) => {
      const cell = getCell(selection.unitId, selection.periodIds[0] ?? '');
      if (!cell?.reservationId || (!canCancelReservation && !canForceReleaseReservation)) return;
      const resolvedForce =
        force === false
          ? isForceRequired(cell, canForceReleaseReservation)
          : (force ?? (canForceReleaseReservation ? true : undefined));
      runAction(() =>
        routeContext.auth.callWithAuth(reservations.cancel, {
          id: cell.reservationId,
          force: resolvedForce,
        }),
      );
    },
    [canCancelReservation, canForceReleaseReservation, getCell, routeContext.auth, runAction],
  );

  return {
    canCreateReservation,
    canCancelReservation,
    canForceReleaseReservation,
    canUpgradeHardReservation,
    canCloseSaleReservation,
    handleCellClick,
    handleReserve,
    handleUpgradeHard,
    handleCloseSale,
    handleRelease,
  };
}
